import { FC, useCallback } from "react";

import { Box } from "@mui/material";
import { CampaignAction } from "@simplyk/common";

import { AmplitudeEventParams, useAmplitude } from "../../../../hooks/amplitude/useAmplitude";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import { useTranslate } from "../../../../hooks/useTranslate";

import { FormTab, FormTabGroup } from "@/forms-design-system/FormTab";
import { FormTypography } from "@/forms-design-system/FormTypography";

export interface FundraisingTypeOptionsProps {
  campaignAction: CampaignAction;
  toggleCampaignAction: (action: CampaignAction) => void;
  allowTeamCreation?: boolean;
  allowFundraiserCreation?: boolean;
  hasTeam?: boolean;
  multipleRegistrant?: boolean;
  amplitudeEvents?: Partial<Record<CampaignAction, AmplitudeEventParams>>;
  showCreateTeamFirst?: boolean;
  allowJoinTeam?: boolean;
}

export const FundraisingTypeOptions: FC<FundraisingTypeOptionsProps> = ({
  campaignAction,
  toggleCampaignAction,
  allowTeamCreation,
  allowFundraiserCreation,
  hasTeam = false,
  multipleRegistrant = false,
  amplitudeEvents,
  showCreateTeamFirst = false,
  allowJoinTeam = true,
}) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const { isSmallScreen } = useMediaQuery();

  const handleCampaignActionToggle = useCallback(
    (action: CampaignAction) => {
      const amplitudeEvent = amplitudeEvents?.[action];
      if (amplitudeEvent) {
        logAmplitudeEvent(amplitudeEvent.event, { ...amplitudeEvent.payload, multipleRegistrant });
      }
      toggleCampaignAction(action);
    },
    [amplitudeEvents, logAmplitudeEvent, multipleRegistrant, toggleCampaignAction]
  );

  const createTeamButton = allowTeamCreation && (
    <FormTab
      label={t("donationForm", "createTeam")}
      value={CampaignAction.CreateTeam}
      data-test="campaign-create-team-button"
    />
  );

  const createFundraiserButton = allowFundraiserCreation && (
    <FormTab
      label={t("donationForm", multipleRegistrant ? "individualCampaigns" : "individualCampaign")}
      value={CampaignAction.IndividualFundraising}
      data-test="campaign-create-individual-fundraising-button"
    />
  );

  const joinTeamButton = (
    <FormTab
      label={t("donationForm", "joinTeam")}
      value={CampaignAction.JoinTeam}
      data-test="campaign-join-team-button"
      onClick={() => handleCampaignActionToggle(CampaignAction.JoinTeam)}
      tooltip={!hasTeam ? t("donationForm", "peerToPeer.tooltip.noTeamsAvailable") : ""}
      disabled={!hasTeam}
    />
  );

  return (
    <Box>
      <FormTypography variant="subtitle2" vibe="intense" sx={{ marginBlockEnd: 1 }}>
        {t("donationForm", "peerToPeer.title.fundraise")}
      </FormTypography>
      <FormTabGroup
        value={campaignAction}
        onChange={(_, newValue) => handleCampaignActionToggle(newValue as CampaignAction)}
        scrollable={isSmallScreen}
      >
        {showCreateTeamFirst ? createTeamButton : createFundraiserButton}
        {allowJoinTeam && joinTeamButton}
        {showCreateTeamFirst ? createFundraiserButton : createTeamButton}
      </FormTabGroup>
    </Box>
  );
};

/*
 */
